import { doc, getDoc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Subscription tier limits
export const SUBSCRIPTION_LIMITS = {
  trial: { searches: 10, users: 3 },
  starter: { searches: 100, users: 3 },
  professional: { searches: 1000, users: 10 }
};

/**
 * Get the current month string for resetting counters
 * @returns {string} Current month in YYYY-MM format
 */
export const getCurrentMonth = () => {
  return new Date().toISOString().slice(0, 7); // "2024-01"
};

/**
 * Check if an organization can perform a search based on their limits
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Object with limit check results
 */
export const checkSearchLimit = async (orgId) => {
  try {
    if (!orgId) {
      return { allowed: false, error: 'Organization ID required' };
    }

    const orgRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    
    if (!orgDoc.exists()) {
      return { allowed: false, error: 'Organization not found' };
    }

    const orgData = orgDoc.data();
    const currentMonth = getCurrentMonth();
    
    // Get subscription tier (default to trial if not set)
    const subscriptionTier = orgData.subscriptionTier || 'trial';
    const tierLimits = SUBSCRIPTION_LIMITS[subscriptionTier];
    
    if (!tierLimits) {
      console.error(`Unknown subscription tier: ${subscriptionTier}`);
      return { allowed: false, error: 'Invalid subscription tier' };
    }

    // Get search limits from organization data
    const searchLimits = orgData.searchLimits || {};
    const monthlyLimit = tierLimits.searches;
    const additionalUsage = orgData.additionalUsage || 0;
    const totalLimit = monthlyLimit + additionalUsage;
    
    // Check if we need to reset for new month
    const lastResetMonth = searchLimits.resetMonth;
    let currentUsage = searchLimits.used || 0;
    
    if (lastResetMonth !== currentMonth) {
      currentUsage = 0; // Reset usage for new month
    }
    
    const remaining = Math.max(0, totalLimit - currentUsage);
    const allowed = currentUsage < totalLimit;
    
    return {
      allowed,
      used: currentUsage,
      limit: monthlyLimit,
      additionalUsage,
      totalLimit,
      remaining,
      subscriptionTier,
      needsReset: lastResetMonth !== currentMonth
    };
  } catch (error) {
    console.error('Error checking search limit:', error);
    return { 
      allowed: false, 
      error: 'Failed to check search limits',
      details: error.message 
    };
  }
};

/**
 * Increment search usage for an organization
 * @param {string} orgId - Organization ID
 * @param {number} count - Number of searches to add (default: 1)
 * @returns {Promise<Object>} Updated usage information
 */
export const incrementSearchUsage = async (orgId, count = 1) => {
  try {
    if (!orgId) {
      throw new Error('Organization ID required');
    }

    const currentMonth = getCurrentMonth();
    const orgRef = doc(db, 'organizations', orgId);
    
    return await runTransaction(db, async (transaction) => {
      const orgDoc = await transaction.get(orgRef);
      
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      
      const orgData = orgDoc.data();
      const searchLimits = orgData.searchLimits || {};
      const lastResetMonth = searchLimits.resetMonth;
      
      // Reset usage if new month
      const currentUsage = (lastResetMonth === currentMonth) ? (searchLimits.used || 0) : 0;
      const newUsage = currentUsage + count;
      
      // Update the organization document
      transaction.update(orgRef, {
        'searchLimits.used': newUsage,
        'searchLimits.resetMonth': currentMonth,
        'searchLimits.lastUpdated': serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return {
        success: true,
        used: newUsage,
        added: count,
        resetMonth: currentMonth
      };
    });
  } catch (error) {
    console.error('Error incrementing search usage:', error);
    throw error;
  }
};

/**
 * Set subscription tier and limits for an organization
 * @param {string} orgId - Organization ID
 * @param {string} tier - Subscription tier (trial, starter, professional)
 * @returns {Promise<boolean>} Success status
 */
export const setSubscriptionTier = async (orgId, tier) => {
  try {
    if (!orgId || !tier) {
      throw new Error('Organization ID and tier are required');
    }

    if (!SUBSCRIPTION_LIMITS[tier]) {
      throw new Error(`Invalid subscription tier: ${tier}`);
    }

    const orgRef = doc(db, 'organizations', orgId);
    
    await updateDoc(orgRef, {
      subscriptionTier: tier,
      updatedAt: serverTimestamp()
    });

    console.log(`Updated organization ${orgId} to ${tier} tier`);
    return true;
  } catch (error) {
    console.error('Error setting subscription tier:', error);
    throw error;
  }
};

/**
 * Set additional usage allowance for an organization (manual override)
 * @param {string} orgId - Organization ID
 * @param {number} additionalUsage - Additional searches allowed (can be negative)
 * @returns {Promise<boolean>} Success status
 */
export const setAdditionalUsage = async (orgId, additionalUsage) => {
  try {
    if (!orgId) {
      throw new Error('Organization ID required');
    }

    const orgRef = doc(db, 'organizations', orgId);
    
    await updateDoc(orgRef, {
      additionalUsage: additionalUsage || 0,
      updatedAt: serverTimestamp()
    });

    console.log(`Updated organization ${orgId} additional usage to ${additionalUsage}`);
    return true;
  } catch (error) {
    console.error('Error setting additional usage:', error);
    throw error;
  }
};

/**
 * Get detailed usage statistics for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Detailed usage information
 */
export const getUsageStats = async (orgId) => {
  try {
    const limitCheck = await checkSearchLimit(orgId);
    
    if (!limitCheck.allowed && limitCheck.error) {
      throw new Error(limitCheck.error);
    }

    const percentUsed = limitCheck.totalLimit > 0 
      ? Math.round((limitCheck.used / limitCheck.totalLimit) * 100)
      : 0;

    return {
      ...limitCheck,
      percentUsed,
      isNearLimit: percentUsed >= 90,
      isAtLimit: percentUsed >= 95,
      monthlyResetDate: getCurrentMonth() + '-01'
    };
  } catch (error) {
    console.error('Error getting usage stats:', error);
    throw error;
  }
};

/**
 * Get the current team member count for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<number>} Current active team member count
 */
export const getTeamMemberCount = async (orgId) => {
  try {
    console.log('[getTeamMemberCount] orgId:', orgId);
    if (!orgId) {
      throw new Error('Organization ID required');
    }

    // This function should not be used for non-admins anymore, but log if it is
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(query(usersRef, where('orgId', '==', orgId), where('status', '==', 'active')));
    console.log('[getTeamMemberCount] snapshot.size:', snapshot.size);
    return snapshot.size;
  } catch (error) {
    console.error('[getTeamMemberCount] Error:', error);
    throw error;
  }
};

/**
 * Check if an organization can add more team members based on their limits
 * @param {string} orgId - Organization ID
 * @param {number} memberCount - Number of members to check for (default: 1)
 * @returns {Promise<Object>} Object with limit check results
 */
export const checkTeamMemberLimit = async (orgId, memberCount = 1) => {
  try {
    if (!orgId) {
      return { allowed: false, error: 'Organization ID required' };
    }

    const orgRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgRef);
    if (!orgDoc.exists()) {
      return { allowed: false, error: 'Organization not found' };
    }

    const orgData = orgDoc.data();
    const subscriptionTier = orgData.subscriptionTier || 'trial';
    const tierLimits = SUBSCRIPTION_LIMITS[subscriptionTier];
    if (!tierLimits) {
      console.error(`[checkTeamMemberLimit] Unknown subscription tier: ${subscriptionTier}`);
      return { allowed: false, error: 'Invalid subscription tier' };
    }

    // Use org-level memberCount - ensure it's a number
    const currentCount = parseInt(orgData.memberCount) || 0;
    const memberLimit = parseInt(tierLimits.users);
    const memberCountToAdd = parseInt(memberCount);
    const remaining = Math.max(0, memberLimit - currentCount);
    const wouldExceed = (currentCount + memberCountToAdd) > memberLimit;
    const allowed = !wouldExceed;

    return {
      allowed,
      currentCount,
      limit: memberLimit,
      remaining,
      subscriptionTier,
      memberCount: memberCountToAdd,
      wouldExceed
    };
  } catch (error) {
    console.error('[checkTeamMemberLimit] Error:', error);
    return { 
      allowed: false, 
      error: 'Failed to check team member limits',
      details: error.message 
    };
  }
};

/**
 * Validate if an organization can add a team member
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Validation result
 */
export const validateTeamMemberAddition = async (orgId) => {
  return await checkTeamMemberLimit(orgId, 1);
};

/**
 * Get team member usage statistics for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Team member usage information
 */
export const getTeamMemberStats = async (orgId) => {
  try {
    const limitCheck = await checkTeamMemberLimit(orgId, 0);
    
    if (!limitCheck.allowed && limitCheck.error) {
      throw new Error(limitCheck.error);
    }

    const percentUsed = limitCheck.limit > 0 
      ? Math.round((limitCheck.currentCount / limitCheck.limit) * 100)
      : 0;

    return {
      ...limitCheck,
      percentUsed,
      isNearLimit: percentUsed >= 90,
      isAtLimit: percentUsed >= 100,
      canAddMore: limitCheck.remaining > 0
    };
  } catch (error) {
    console.error('Error getting team member stats:', error);
    throw error;
  }
}; 